/**
 * AssignPromptModal - Prompt shown when a todo contains @mentions
 *
 * For each mentioned person, asks the creator:
 * "Keep yourself on this todo too, or hand it off?"
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, UserPlus, LogOut, X } from 'lucide-react';
import type { MentionInput } from '@/types/mention.types';

interface AssignPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (keepCreator: boolean) => void;
  mentions: MentionInput[];
}

export function AssignPromptModal({
  isOpen,
  onClose,
  onConfirm,
  mentions,
}: AssignPromptModalProps) {
  const [keepCreator, setKeepCreator] = useState(true);

  if (!isOpen) return null;

  const uniqueNames = [...new Set(mentions.map((m) => m.userName))];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/40 z-[200] flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 8 }}
            transition={{ duration: 0.15 }}
            className="bg-surface-card border border-theme rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-theme-soft">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-blue-400" />
                <span className="text-sm font-semibold text-theme-primary">Assign Todo</span>
              </div>
              <button
                onClick={onClose}
                className="p-1 rounded-lg hover:bg-surface-hover text-theme-muted"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="px-5 py-4 space-y-4">
              <p className="text-sm text-theme-secondary">
                Assigning to{' '}
                <span className="font-medium text-blue-400">
                  {uniqueNames.join(', ')}
                </span>
              </p>

              {/* Keep/Handoff toggle */}
              <div className="space-y-2">
                <button
                  onClick={() => setKeepCreator(true)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-all text-left
                    ${keepCreator
                      ? 'border-emerald-400/30 bg-emerald-500/[0.06]'
                      : 'border-theme-soft hover:border-theme'
                    }
                  `}
                >
                  <UserPlus className={`w-4 h-4 ${keepCreator ? 'text-emerald-400' : 'text-theme-muted'}`} />
                  <div>
                    <span className={`text-sm font-medium ${keepCreator ? 'text-emerald-400' : 'text-theme-primary'}`}>
                      Keep me on it too
                    </span>
                    <p className="text-[11px] text-theme-muted mt-0.5">
                      You and {uniqueNames.join(', ')} will both see this todo
                    </p>
                  </div>
                </button>

                <button
                  onClick={() => setKeepCreator(false)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-all text-left
                    ${!keepCreator
                      ? 'border-amber-400/30 bg-amber-500/[0.06]'
                      : 'border-theme-soft hover:border-theme'
                    }
                  `}
                >
                  <LogOut className={`w-4 h-4 ${!keepCreator ? 'text-amber-400' : 'text-theme-muted'}`} />
                  <div>
                    <span className={`text-sm font-medium ${!keepCreator ? 'text-amber-400' : 'text-theme-primary'}`}>
                      Hand it off
                    </span>
                    <p className="text-[11px] text-theme-muted mt-0.5">
                      Only {uniqueNames.join(', ')} will see this todo
                    </p>
                  </div>
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="px-5 py-3 border-t border-theme-soft flex justify-end gap-2">
              <button
                onClick={onClose}
                className="px-3 py-1.5 text-sm text-theme-muted hover:text-theme-primary transition-colors rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => onConfirm(keepCreator)}
                className="px-4 py-1.5 text-sm font-medium bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
              >
                Assign
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
